import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { useAuth } from "@/lib/auth";
import Modal from "./Modal";
import { Lock, ShieldAlert } from "lucide-react";

/**
 * Force-shown after first login when must_change_password is true.
 * Locks the user inside the modal — no close button — until they set a new
 * password. After success, refreshes the auth user so must_change_password
 * flips to false and they can continue.
 */
export default function ChangePasswordModal({ onDone }) {
  const toast = useToast();
  const { refresh } = useAuth();
  const [current, setCurrent] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw1.length < 8) { setErr("New password must be at least 8 characters."); return; }
    if (pw1 !== pw2) { setErr("New passwords do not match."); return; }
    if (pw1 === current) { setErr("New password must be different from the temporary one."); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: pw1 });
      await refresh();
      toast.success("Password updated.");
      onDone?.();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // No onClose — the modal is non-dismissable on purpose. Esc / backdrop click are no-ops.
    <Modal
      title="Set a new password"
      icon={<ShieldAlert className="w-5 h-5 text-amber-600" />}
      onClose={() => {}}
      size="md"
    >
      <form onSubmit={submit} className="px-6 py-5 space-y-4">
        <p className="text-sm text-zinc-600">
          Your account is using a temporary password. Please set a permanent one to continue.
        </p>
        <label className="block">
          <span className="es-label">Current (temporary) password</span>
          <div className="relative">
            <Lock className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="password"
              required
              autoFocus
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="es-input pl-9 w-full"
              data-testid="change-pw-current"
            />
          </div>
        </label>
        <label className="block">
          <span className="es-label">New password</span>
          <input
            type="password"
            required
            minLength={8}
            value={pw1}
            onChange={(e) => setPw1(e.target.value)}
            className="es-input"
            data-testid="change-pw-new"
          />
        </label>
        <label className="block">
          <span className="es-label">Confirm new password</span>
          <input
            type="password"
            required
            minLength={8}
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            className="es-input"
            data-testid="change-pw-confirm"
          />
        </label>
        {err && <div className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-lg">{err}</div>}
        <button
          type="submit"
          disabled={submitting}
          className="es-btn-primary w-full disabled:opacity-60"
          data-testid="change-pw-submit"
        >
          {submitting ? "Updating…" : "Update password"}
        </button>
      </form>
    </Modal>
  );
}
